/**
 * Applies global inbox SignalR events (read/unread/updated) to container
 * home-feed / section folder trees so other tabs/windows stay in sync
 * with the inbox hub — not only `OnContainerEvent`.
 */
import type { ProjectContainerSectionModel } from 'src/features/companyProjects/projectContainers/types/ProjectContainerHomeTypes';
import type { ProjectIntakeEvent } from 'src/features/companyProjects/projectIntake/inbox/types';
import { ProjectIntakeEventType } from 'src/features/companyProjects/projectIntake/inbox/types';

const getIntakeId = (event: ProjectIntakeEvent): number | undefined => {
  const raw = event.intakeId ?? event.data?.Id;
  if (raw == null) return undefined;
  return typeof raw === 'number' ? raw : Number(raw);
};

const patchHasNewMessageInSections = (
  sections: ProjectContainerSectionModel[],
  intakeId: number,
  hasNewMessage: boolean
): ProjectContainerSectionModel[] =>
  sections.map((section) => ({
    ...section,
    Folders: section.Folders?.map((folder) => {
      const current = folder.Intakes?.find((item) => item.Id === intakeId);
      if (!current || current.HasNewMessage === hasNewMessage) {
        return folder;
      }

      const unreadCount = folder.UnreadCount ?? 0;
      return {
        ...folder,
        UnreadCount: Math.max(0, unreadCount + (hasNewMessage ? 1 : -1)),
        Intakes: folder.Intakes?.map((item) =>
          item.Id === intakeId ? { ...item, HasNewMessage: hasNewMessage } : item
        )
      };
    })
  }));

/** Drop an intake from every folder (e.g. merge or inbox removal) — mirrors `IntakeRemoved` in container hub events. */
const removeIntakeFromSections = (
  sections: ProjectContainerSectionModel[],
  intakeId: number
): ProjectContainerSectionModel[] =>
  sections.map((section) => ({
    ...section,
    Folders: section.Folders?.map((folder) => {
      const removed = folder.Intakes?.find((item) => item.Id === intakeId);
      if (!removed) {
        return folder;
      }

      return {
        ...folder,
        IntakeCount: Math.max(0, (folder.IntakeCount ?? folder.Intakes?.length ?? 0) - 1),
        UnreadCount: Math.max(0, (folder.UnreadCount ?? 0) - (removed.HasNewMessage ? 1 : 0)),
        Intakes: folder.Intakes?.filter((item) => item.Id !== intakeId)
      };
    })
  }));

const mergeUpdatedIntakeInSections = (
  sections: ProjectContainerSectionModel[],
  intakeId: number,
  event: ProjectIntakeEvent
): ProjectContainerSectionModel[] => {
  const data = event.data;
  if (!data) return sections;

  return sections.map((section) => ({
    ...section,
    Folders: section.Folders?.map((folder) => {
      const current = folder.Intakes?.find((item) => item.Id === intakeId);
      if (!current) {
        return folder;
      }

      const hasNewMessage = data.HasNewMessage ?? current.HasNewMessage;
      const unreadDelta = (hasNewMessage ? 1 : 0) - (current.HasNewMessage ? 1 : 0);
      return {
        ...folder,
        UnreadCount: Math.max(0, (folder.UnreadCount ?? 0) + unreadDelta),
        Intakes: folder.Intakes?.map((item) => {
          if (item.Id !== intakeId) return item;
          return {
            ...item,
            Subject: data.Subject ?? item.Subject,
            HasNewMessage: hasNewMessage
          };
        })
      };
    })
  }));
};

/**
 * Patch all open container section trees when an `OnInboxEvent` arrives
 * (same payload as inbox list). Read/Unread/Updated affect folder "New" tags.
 */
export function applyInboxEventToContainerSectionModels(
  sections: ProjectContainerSectionModel[] | undefined,
  event: ProjectIntakeEvent
): ProjectContainerSectionModel[] | undefined {
  if (!sections?.length) return sections;

  const intakeId = getIntakeId(event);
  if (intakeId === undefined || !Number.isFinite(intakeId)) return sections;

  switch (event.type) {
    case ProjectIntakeEventType.Read:
      return patchHasNewMessageInSections(sections, intakeId, false);
    case ProjectIntakeEventType.Unread:
      return patchHasNewMessageInSections(sections, intakeId, true);
    case ProjectIntakeEventType.Updated:
      return mergeUpdatedIntakeInSections(sections, intakeId, event);
    case ProjectIntakeEventType.Removed:
    case ProjectIntakeEventType.Merged:
      return removeIntakeFromSections(sections, intakeId);
    default:
      return sections;
  }
}

export function applyInboxEventToSingleSectionModel(
  section: ProjectContainerSectionModel | undefined,
  event: ProjectIntakeEvent
): ProjectContainerSectionModel | undefined {
  if (!section) return section;
  const next = applyInboxEventToContainerSectionModels([section], event);
  return next?.[0];
}
